import { FC, ReactElement } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { tokens } from "@/tokens";
import { responsiveFontSizes } from "@/utils/responsive";
import type { IeepaAnalysisReport } from "@/types/ieepa";

import type { BucketSubSplit, CapeAnalyticsResult } from "./useCapeAnalytics";

/** Indicator colors — grayscale, deliberately neutral vs window hues. */
const IND = {
  recon: "#9CA3AF",
  protest: "#4B5563",
  both: "#111827",
} as const;

const fmtMoney = (v: number): string =>
  `$${v.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
const fmtMoneyFull = (v: number): string =>
  `$${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface FunnelBarProps {
  toneColor: string;
  toneLabel: string;
  totalEntryCount: number;
  bucketEntryCount: number;
  bucketIeepaDuty: number;
  split: BucketSubSplit;
}

const subBucketTitle = {
  clean: "No indicator flags",
  recon: "Has recon indicator",
  protest: "Has existing protest",
  both: "Has recon AND existing protest",
} as const;

const SegmentTooltip: FC<{
  label: string;
  count: number;
  ieepaDuty: number;
}> = ({ label, count, ieepaDuty }) => (
  <>
    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: "white" }}>
      {count.toLocaleString()} entries · {fmtMoney(ieepaDuty)} IEEPA
    </Typography>
    <Typography sx={{ fontSize: tokens.fontSize.xs, color: "rgba(255,255,255,0.75)" }}>
      {label}
    </Typography>
  </>
);

const FunnelBar: FC<FunnelBarProps> = ({
  toneColor,
  toneLabel,
  totalEntryCount,
  bucketEntryCount,
  bucketIeepaDuty,
  split,
}): ReactElement => {
  // All sub-widths measured against the grand total → shared scale across bars.
  const pct = (n: number): number =>
    totalEntryCount > 0 ? (n / totalEntryCount) * 100 : 0;

  const bucketPct = pct(bucketEntryCount);

  const segs: Array<{
    key: keyof BucketSubSplit;
    count: number;
    ieepaDuty: number;
    color: string;
    showLabel: boolean;
  }> = [
    { key: "clean",   ...split.clean,   color: toneColor,    showLabel: true  },
    { key: "recon",   ...split.recon,   color: IND.recon,    showLabel: false },
    { key: "protest", ...split.protest, color: IND.protest,  showLabel: false },
    { key: "both",    ...split.both,    color: IND.both,     showLabel: false },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "150px 1fr 140px", md: "170px 1fr 200px" },
        alignItems: "center",
        gap: { xs: 0.5, sm: 1.5 },
      }}
    >
      {/* Left: label column */}
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: toneColor }} />
          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body }}>
            {toneLabel}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 10.5, color: tokens.text.muted, pl: 1.75 }}>
          {bucketEntryCount.toLocaleString()} entries · {bucketPct.toFixed(1)}% of book
        </Typography>
      </Stack>

      {/* Middle: segmented track */}
      <Box
        sx={{
          height: 26,
          bgcolor: tokens.bg.muted,
          borderRadius: "4px",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {segs.map((s) => {
          if (s.count === 0) return null;
          const widthPct = pct(s.count);
          return (
            <Tooltip
              key={s.key}
              arrow
              title={
                <SegmentTooltip
                  label={subBucketTitle[s.key]}
                  count={s.count}
                  ieepaDuty={s.ieepaDuty}
                />
              }
            >
              <Box
                tabIndex={0}
                aria-label={`${s.count.toLocaleString()} entries, ${fmtMoney(s.ieepaDuty)} IEEPA duty, ${subBucketTitle[s.key]}`}
                sx={{
                  height: "100%",
                  width: `${widthPct}%`,
                  bgcolor: s.color,
                  display: "flex",
                  alignItems: "center",
                  cursor: "help",
                  transition: "filter 120ms",
                  "&:hover": { filter: "brightness(0.92)" },
                  "&:focus-visible": {
                    outline: `2px solid ${s.color}`,
                    outlineOffset: "2px",
                  },
                }}
              >
                {s.showLabel && s.count > 0 && (
                  <Typography
                    sx={{
                      fontSize: tokens.fontSize.xs,
                      fontWeight: tokens.fontWeight.semibold,
                      color: "black",
                      px: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.count.toLocaleString()} clean
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Right: $ column */}
      <Box sx={{ textAlign: { xs: "left", sm: "right" }, fontVariantNumeric: "tabular-nums" }}>
        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body }}>
          {fmtMoney(bucketIeepaDuty)}
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
          IEEPA duty
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Single-segment CAPE status bar. Used three times on the Funnel:
 *   1. "CAPE Certified" — entries CBP already refunded; $ = refundAmount.
 *   2. "CAPE Pending" — entries with paid − Σ line > 0; $ = expectedRefund.
 *   3. "CAPE Eligible" — entries with no paid − line measurement; $ = ieepaDuty.
 *
 * All bars share `totalEntryCount` so widths are comparable across rows.
 * The Pending identity (paid − line) is the one validated to the cent
 * against the 9 certified Original Brands CBP refunds.
 */
interface AlertSubSegment {
  count: number;
  amount: number;
  color: string;
  segmentLabel: string;
  tooltipLabel: string;
}

interface CapeStatusBarProps {
  toneColor: string;
  toneLabel: string;
  toneSublabel: string;
  rightCaption: string;
  opacity?: number;
  totalEntryCount: number;
  bucketEntryCount: number;
  bucketAmount: number;
  segmentLabel: string;
  tooltipLabel: string;
  /** Optional sub-segment overlaid on the bar (e.g. CBP-rejected pending claims). */
  alertSegment?: AlertSubSegment;
  /**
   * Optional indicator split (clean / recon / protest / both). When provided
   * and any indicator segment has entries, the bar renders as a 4-segment
   * bar (matching FunnelBar) instead of the single-segment fill. Used on the
   * CAPE Eligible bar so users can see which eligible entries carry recon
   * or existing-protest flags before deciding to file.
   */
  split?: BucketSubSplit;
}

const CapeStatusBar: FC<CapeStatusBarProps> = ({
  toneColor,
  toneLabel,
  toneSublabel,
  rightCaption,
  opacity = 1,
  totalEntryCount,
  bucketEntryCount,
  bucketAmount,
  segmentLabel,
  tooltipLabel,
  alertSegment,
  split,
}): ReactElement => {
  const pct = (n: number): number =>
    totalEntryCount > 0 ? (n / totalEntryCount) * 100 : 0;
  const bucketPct = pct(bucketEntryCount);
  // Cap the alert segment at the bucket count so we never overdraw if the
  // analytics layer ever counts a rejected entry that didn't make it into
  // the parent pending set.
  const alertCount = alertSegment
    ? Math.min(alertSegment.count, bucketEntryCount)
    : 0;
  const mainCount = Math.max(0, bucketEntryCount - alertCount);

  // Indicator-split mode is only enabled when at least one indicator segment
  // has entries — otherwise the single-segment fill is identical and avoids
  // an extra DOM/Tooltip wrapper. alertSegment + split are mutually exclusive
  // by design: today only the Pending bar uses alertSegment and only the
  // Eligible bar uses split.
  const showSplit =
    !alertSegment &&
    !!split &&
    split.recon.count + split.protest.count + split.both.count > 0;
  const splitSegs = showSplit && split
    ? ([
        { key: "clean",   ...split.clean,   color: alpha(toneColor, opacity), showLabel: true  },
        { key: "recon",   ...split.recon,   color: IND.recon,                 showLabel: false },
        { key: "protest", ...split.protest, color: IND.protest,               showLabel: false },
        { key: "both",    ...split.both,    color: IND.both,                  showLabel: false },
      ] as Array<{
        key: keyof BucketSubSplit;
        count: number;
        ieepaDuty: number;
        expectedRefund: number;
        color: string;
        showLabel: boolean;
      }>)
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "150px 1fr 140px", md: "170px 1fr 200px" },
        alignItems: "center",
        gap: { xs: 0.5, sm: 1.5 },
      }}
    >
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: toneColor, opacity }} />
          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body }}>
            {toneLabel}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 10.5, color: tokens.text.muted, pl: 1.75 }}>
          {bucketEntryCount.toLocaleString()} entries · {bucketPct.toFixed(1)}% of book
        </Typography>
        {toneSublabel && (
          <Typography sx={{ fontSize: 10.5, color: tokens.text.muted, pl: 1.75, fontStyle: "italic" }}>
            {toneSublabel}
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          height: 26,
          bgcolor: tokens.bg.muted,
          borderRadius: "4px",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {splitSegs &&
          splitSegs.map((s) => {
            if (s.count === 0) return null;
            const widthPct = pct(s.count);
            return (
              <Tooltip
                key={s.key}
                arrow
                title={
                  <SegmentTooltip
                    label={subBucketTitle[s.key]}
                    count={s.count}
                    ieepaDuty={s.ieepaDuty}
                  />
                }
              >
                <Box
                  tabIndex={0}
                  aria-label={`${s.count.toLocaleString()} entries, ${fmtMoney(s.ieepaDuty)} IEEPA duty, ${subBucketTitle[s.key]}`}
                  sx={{
                    height: "100%",
                    width: `${widthPct}%`,
                    bgcolor: s.color,
                    display: "flex",
                    alignItems: "center",
                    cursor: "help",
                    transition: "filter 120ms",
                    "&:hover": { filter: "brightness(0.92)" },
                    "&:focus-visible": {
                      outline: `2px solid ${s.color}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {s.showLabel && s.count > 0 && (
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.xs,
                        fontWeight: tokens.fontWeight.semibold,
                        color: "black",
                        px: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.count.toLocaleString()} clean
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        {!splitSegs && mainCount > 0 && (
          <Tooltip
            arrow
            title={
              <>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: "white" }}>
                  {mainCount.toLocaleString()} entries · {fmtMoney(bucketAmount - (alertSegment?.amount ?? 0))}
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: "rgba(255,255,255,0.75)" }}>
                  {tooltipLabel}
                </Typography>
              </>
            }
          >
            <Box
              tabIndex={0}
              aria-label={`${mainCount.toLocaleString()} entries, ${fmtMoney(bucketAmount - (alertSegment?.amount ?? 0))} ${tooltipLabel}`}
              sx={{
                height: "100%",
                width: `${pct(mainCount)}%`,
                // Apply opacity to the fill color (rgba) instead of the Box's
                // CSS `opacity`, otherwise the segment label inherits the
                // fade and "black" reads as grey on light bars (Eligible 0.35).
                bgcolor: alpha(toneColor, opacity),
                display: "flex",
                alignItems: "center",
                cursor: "help",
                transition: "filter 120ms",
                "&:hover": { filter: "brightness(0.92)" },
                "&:focus-visible": {
                  outline: `2px solid ${toneColor}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.fontSize.xs,
                  fontWeight: tokens.fontWeight.semibold,
                  // All in-bar labels render black so they stay legible across
                  // the full opacity range (Eligible 0.35 → Certified 1.0)
                  // without getting clipped by white-on-light-fill drops.
                  color: "black",
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {alertCount > 0
                  ? `${mainCount.toLocaleString()} clean`
                  : segmentLabel}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {alertSegment && alertCount > 0 && (
          <Tooltip
            arrow
            title={
              <>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: "white" }}>
                  {alertCount.toLocaleString()} entries · {fmtMoney(alertSegment.amount)}
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: "rgba(255,255,255,0.75)" }}>
                  {alertSegment.tooltipLabel}
                </Typography>
              </>
            }
          >
            <Box
              tabIndex={0}
              aria-label={`${alertCount.toLocaleString()} entries, ${fmtMoney(alertSegment.amount)} ${alertSegment.tooltipLabel}`}
              sx={{
                height: "100%",
                width: `${pct(alertCount)}%`,
                bgcolor: alertSegment.color,
                display: "flex",
                alignItems: "center",
                cursor: "help",
                transition: "filter 120ms",
                "&:hover": { filter: "brightness(0.92)" },
                "&:focus-visible": {
                  outline: `2px solid ${alertSegment.color}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.fontSize.xs,
                  fontWeight: tokens.fontWeight.semibold,
                  color: "black",
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {alertSegment.segmentLabel}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ textAlign: { xs: "left", sm: "right" }, fontVariantNumeric: "tabular-nums" }}>
        {alertSegment && alertCount > 0 ? (
          <>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.body,
                whiteSpace: "nowrap",
              }}
            >
              {fmtMoney(bucketAmount - alertSegment.amount)}
              <Box
                component="span"
                sx={{
                  color: tokens.text.muted,
                  fontWeight: tokens.fontWeight.normal,
                  fontSize: tokens.fontSize.xs,
                  ml: 0.5,
                }}
              >
                {rightCaption}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                color: alertSegment.color,
                whiteSpace: "nowrap",
                mt: 0.25,
              }}
            >
              {fmtMoney(alertSegment.amount)}
              <Box
                component="span"
                sx={{
                  color: alertSegment.color,
                  fontWeight: tokens.fontWeight.normal,
                  fontSize: tokens.fontSize.xs,
                  ml: 0.5,
                }}
              >
                Rejected
              </Box>
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body }}>
              {fmtMoney(bucketAmount)}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
              {rightCaption}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

interface FunnelOverviewProps {
  report: IeepaAnalysisReport;
  analytics: CapeAnalyticsResult;
}

export const FunnelOverview: FC<FunnelOverviewProps> = ({
  report,
  analytics,
}): ReactElement => {
  const {
    bucketSplits,
    totalEntryCount,
    capePendingEntries,
    capePendingRejectedEntries,
    pendingRejectedRefund,
    capeEligibleOnlyEntries,
    capeEligibleOnlySplit,
    pendingProjectedRefund,
    eligibleOnlyProjectedRefund,
    refundedAmount,
    refundedEntries,
    refundedInterestAmount,
    capePhase1TotalAmount,
    capePhase1TotalEntries,
  } = analytics;

  const protestBucket = analytics.tiles.find((t) => t.key === "PROTEST_RECOMMENDED");
  const oowBucket    = analytics.tiles.find((t) => t.key === "OUT_OF_WINDOW");

  const protestOverlayCount = analytics.hasProtestEntries.length;

  // Shared bar denominator. Refunded entries can sit outside the IEEPA
  // funnel (their Ch99DutyDict was empty), so the CAPE Phase 1 row may
  // exceed totalEntryCount. Stretch the denominator so all bars stay
  // consistent at <= 100%.
  const barDenominator = Math.max(totalEntryCount, capePhase1TotalEntries);

  // Hide the indicator legend + footnote when the book has no recon flags
  // and no existing protests (i.e. the indicator sub-segments would all
  // be zero anyway — adding a legend would just be noise).
  const indicatorCount =
    bucketSplits.CAPE.recon.count +
    bucketSplits.CAPE.protest.count +
    bucketSplits.CAPE.both.count +
    bucketSplits.PROTEST_RECOMMENDED.recon.count +
    bucketSplits.PROTEST_RECOMMENDED.protest.count +
    bucketSplits.PROTEST_RECOMMENDED.both.count +
    bucketSplits.OUT_OF_WINDOW.recon.count +
    bucketSplits.OUT_OF_WINDOW.protest.count +
    bucketSplits.OUT_OF_WINDOW.both.count;
  const hasIndicators = indicatorCount > 0;

  // Refund date for the hero: date only, no time suffix (CBP can send
  // "YYYY/MM/DD HH:MM:SS" or variants with milliseconds).
  const latestRefundDateFormatted = ((): string => {
    const raw = report.totals.capeRefundLatestDate;
    if (!raw?.trim()) return "";

    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];

    let head = raw.trim();
    head = head
      .replace(/T\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?$/i, "")
      .replace(/\s+\d{1,2}:\d{2}(:\d{2})?(\.\d+)?$/i, "");

    const slash = head.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
    if (slash) {
      const [, y, mo, dd] = slash;
      return `${months[Number(mo) - 1]} ${Number(dd)}, ${y}`;
    }

    const dash = head.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (dash) {
      const [, y, mo, dd] = dash;
      return `${months[Number(mo) - 1]} ${Number(dd)}, ${y}`;
    }

    return "";
  })();

  return (
    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Box
        sx={{
          p: { xs: 1.75, sm: 2.25, md: 2.75 },
          bgcolor: tokens.bg.surface,
          border: `1px solid ${tokens.border.default}`,
          borderRadius: tokens.radius.lg,
        }}
      >
        {/* Hero — total CAPE Phase 1 refund (Approved Refund + potential) */}
        <Box
          sx={{
            mb: { xs: 2, md: 2.5 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: { xs: 1.25, sm: 2 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.muted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.75,
              }}
            >
              CAPE Phase 1 Refund
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: responsiveFontSizes["3xl"],
                fontWeight: tokens.fontWeight.heavy,
                color: tokens.status.success,
                lineHeight: 1.1,
              }}
            >
              {fmtMoneyFull(capePhase1TotalAmount)}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mt: 1 }}>
              <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>
                {fmtMoney(refundedAmount)}
              </b>{" "}
              CAPE Certified ({refundedEntries.toLocaleString()} entries)
              {" · "}
              <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>
                {fmtMoney(pendingProjectedRefund)}
              </b>{" "}
              CAPE Pending ({capePendingEntries.length.toLocaleString()} entries)
              {" · "}
              <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>
                {fmtMoney(eligibleOnlyProjectedRefund)}
              </b>{" "}
              CAPE Eligible ({capeEligibleOnlyEntries.length.toLocaleString()} entries)
              {refundedInterestAmount > 0 && (
                <>
                  {" · "}
                  {fmtMoney(refundedInterestAmount)} interest paid
                </>
              )}
            </Typography>
          </Box>
          {latestRefundDateFormatted && (
            <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Refund date
              </Typography>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.lg,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.body,
                  fontVariantNumeric: "tabular-nums",
                  mt: 0.5,
                }}
              >
                {latestRefundDateFormatted}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bars */}
        <Stack spacing={1.75}>
          <CapeStatusBar
            toneColor={tokens.status.success}
            toneLabel="CAPE Certified"
            toneSublabel="Refund issued by CBP"
            rightCaption="Approved refund"
            totalEntryCount={barDenominator}
            bucketEntryCount={refundedEntries}
            bucketAmount={refundedAmount}
            segmentLabel={`${refundedEntries.toLocaleString()} certified`}
            tooltipLabel="Already refunded by CBP"
          />
          <CapeStatusBar
            toneColor={tokens.status.success}
            toneLabel="CAPE Pending"
            toneSublabel="paid duty − Σ line tariff duty"
            rightCaption="Expected refund"
            opacity={0.65}
            totalEntryCount={barDenominator}
            bucketEntryCount={capePendingEntries.length}
            bucketAmount={pendingProjectedRefund}
            segmentLabel={`${capePendingEntries.length.toLocaleString()} pending`}
            tooltipLabel="Empirical refund: totalPaidDuty − Σ line tariff duty"
            alertSegment={
              capePendingRejectedEntries.length > 0
                ? {
                    count: capePendingRejectedEntries.length,
                    amount: pendingRejectedRefund,
                    color: tokens.status.danger,
                    segmentLabel: `${capePendingRejectedEntries.length.toLocaleString()} rejected`,
                    tooltipLabel: "Claim rejected by CBP — needs attention",
                  }
                : undefined
            }
          />
          <CapeStatusBar
            toneColor={tokens.status.success}
            toneLabel="CAPE Eligible"
            toneSublabel="IEEPA duty still on entry"
            rightCaption="Potential refund"
            opacity={0.35}
            totalEntryCount={barDenominator}
            bucketEntryCount={capeEligibleOnlyEntries.length}
            bucketAmount={eligibleOnlyProjectedRefund}
            segmentLabel={`${capeEligibleOnlyEntries.length.toLocaleString()} eligible`}
            tooltipLabel="No paid−line measurement; sum of IEEPA duties still on the entry"
            split={capeEligibleOnlySplit}
          />
          <FunnelBar
            toneColor={tokens.status.warning}
            toneLabel="Protest Recommended"
            totalEntryCount={barDenominator}
            bucketEntryCount={protestBucket?.entryCount ?? 0}
            bucketIeepaDuty={protestBucket?.ieepaDuty ?? 0}
            split={bucketSplits.PROTEST_RECOMMENDED}
          />
          <FunnelBar
            toneColor={tokens.status.danger}
            toneLabel="Out of Window"
            totalEntryCount={barDenominator}
            bucketEntryCount={oowBucket?.entryCount ?? 0}
            bucketIeepaDuty={oowBucket?.ieepaDuty ?? 0}
            split={bucketSplits.OUT_OF_WINDOW}
          />
        </Stack>

        {/* Window definitions */}
        <Box
          sx={{
            mt: 2,
            pt: 1.75,
            borderTop: `1px dashed ${tokens.border.default}`,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2.5,
          }}
        >
          <WindowDef color={tokens.status.success} term="CAPE Phase 1">
            Unliquidated OR liquidated ≤ 80 days before the filing date. Submittable via the CAPE portal.
          </WindowDef>
          <WindowDef color={tokens.status.warning} term="Protest Recommended">
            Liquidated 81–180 days. Past CAPE cutoff but formal protest still filable under 19 CFR § 174.12(e).
          </WindowDef>
          <WindowDef color={tokens.status.danger} term="Out of Window">
            Liquidated &gt; 180 days. Past the protest window — no recovery path remains.
          </WindowDef>
        </Box>

        {/* Indicator footnote — paired with the legend, gated together. */}
        {hasIndicators && (
          <Box
            sx={{
              mt: 1.75,
              pt: 1.5,
              borderTop: `1px dashed ${tokens.border.default}`,
              fontSize: tokens.fontSize.xs,
              color: tokens.text.muted,
              lineHeight: 1.6,
            }}
          >
            <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>
              Indicators
            </b>{" "}
            cross-cut all three windows.{" "}
            <MiniSwatch color={IND.recon} />
            <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>Recon</b>
            {" "}— enrolled in reconciliation; does <u>not</u> disqualify from Phase 1.
            &nbsp;·&nbsp;
            <MiniSwatch color={IND.protest} />
            <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>Has existing protest</b>
            {" "}— already has a Protest Number on ACE. Excluded from potential refund by default.
            &nbsp;·&nbsp;
            <MiniSwatch color={IND.both} />
            <b style={{ color: tokens.text.body, fontWeight: tokens.fontWeight.bold }}>Both</b>
            {" "}— flagged as recon AND existing protest.
          </Box>
        )}
      </Box>

      {/* Caveat strip — only when book has protests */}
      {report.bookHasProtest && (
        <Box
          sx={{
            bgcolor: tokens.bg.surface,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            px: 1.75,
            py: 1.25,
            fontSize: tokens.fontSize.sm,
            color: tokens.text.body,
          }}
        >
          <b style={{ fontWeight: tokens.fontWeight.bold }}>
            {protestOverlayCount.toLocaleString()} entries carry existing protests
          </b>{" "}
          — excluded from the projected refund above. Coordinate with counsel before filing. The export dialog includes a toggle to add them back if needed for modeling.
        </Box>
      )}
    </Stack>
  );
};

const WindowDef: FC<{ color: string; term: string; children: React.ReactNode }> = ({
  color,
  term,
  children,
}) => (
  <Box sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.secondary, lineHeight: 1.5 }}>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.4 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color }} />
      <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
        {term}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const MiniSwatch: FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 14,
      height: 10,
      borderRadius: "2px",
      verticalAlign: "middle",
      mx: "3px",
      mb: "1px",
      bgcolor: color,
    }}
  />
);

export default FunnelOverview;
